import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API_BASE, formatDate, formatCurrency } from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Vault, Shield, Clock, AlertTriangle, CheckCircle2, Mail,
  Phone, FileText, Landmark, TrendingUp, Home, Lock,
} from "lucide-react";

export default function SharedVault() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API_BASE}/public/share/${token}`)
      .then((r) => { if (!cancelled) setData(r.data); })
      .catch((e) => {
        if (!cancelled) {
          const detail = e?.response?.data?.detail || e.message;
          setError(typeof detail === "string" ? detail : "Unable to open share link");
        }
      })
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [token]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-300 border-t-slate-900" />
    </div>;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
        <Card className="max-w-md w-full p-8 text-center" data-testid="share-error-card">
          <div className="h-12 w-12 rounded-full bg-red-100 text-red-700 mx-auto flex items-center justify-center">
            <AlertTriangle className="h-6 w-6" />
          </div>
          <h1 className="font-heading text-xl font-semibold mt-4">Link not available</h1>
          <p className="text-sm text-slate-600 mt-2">{error}</p>
          <p className="text-xs text-slate-500 mt-6">
            Ask the owner to generate a new share link, or check that you have the complete URL.
          </p>
        </Card>
      </div>
    );
  }

  const expiresAt = new Date(data.expires_at);
  const now = new Date();
  const hoursLeft = Math.max(0, Math.floor((expiresAt - now) / 3600000));
  const totalItems = (data.modules || []).reduce((n, m) => n + m.items.length, 0);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/85 backdrop-blur-xl border-b border-slate-200/70">
        <div className="max-w-5xl mx-auto px-4 sm:px-8 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <div className="h-8 w-8 rounded-md bg-slate-900 flex items-center justify-center shrink-0">
              <Vault className="h-4 w-4 text-white" strokeWidth={1.8} />
            </div>
            <div className="min-w-0">
              <div className="font-heading font-semibold text-slate-900 leading-tight truncate">
                {data.owner?.full_name}'s Vault
              </div>
              <div className="text-[10px] label-overline">Read-only share</div>
            </div>
          </div>
          <Badge variant="outline" className="gap-1">
            <Clock className="h-3 w-3" /> {hoursLeft < 24
              ? `${hoursLeft}h left`
              : `${Math.floor(hoursLeft / 24)}d left`}
          </Badge>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-8 py-8 space-y-8" data-testid="shared-vault-page">
        {/* Hero */}
        <div>
          <div className="label-overline flex items-center gap-2">
            <Shield className="h-3 w-3" /> Secure share from {data.owner?.full_name}
          </div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-2">
            {data.note || "A read-only view of their financial continuity vault"}
          </h1>
          <div className="mt-3 flex flex-wrap gap-2 text-sm">
            <Badge variant="secondary">
              {data.modules.length} module{data.modules.length === 1 ? "" : "s"}
            </Badge>
            <Badge variant="secondary">{totalItems} total entries</Badge>
            {data.reveal_sensitive
              ? <Badge variant="outline" className="text-amber-700 border-amber-300">Sensitive values revealed</Badge>
              : <Badge variant="outline">Sensitive values masked</Badge>}
          </div>
          <div className="mt-2 text-xs text-slate-500">
            Generated {formatDate(data.generated_at)} · Expires {expiresAt.toLocaleString()}
          </div>
        </div>

        {/* Action plan */}
        {data.action_plan && (
          <Card className="p-6 bg-slate-900 text-white" data-testid="share-action-plan">
            <div className="label-overline text-slate-300 flex items-center gap-2">
              <AlertTriangle className="h-3 w-3" /> Emergency Action Plan
            </div>
            {data.action_plan.family_message && (
              <div className="mt-4 bg-slate-800 border border-slate-700 rounded-md p-4">
                <div className="label-overline text-slate-400 mb-2">A message from {data.owner?.full_name}</div>
                <p className="text-sm whitespace-pre-wrap">{data.action_plan.family_message}</p>
              </div>
            )}
            <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              {data.action_plan.will_location && (
                <div>
                  <div className="label-overline text-slate-400">Will location</div>
                  <div className="mt-1">{data.action_plan.will_location}</div>
                </div>
              )}
              {data.action_plan.locker_key_location && (
                <div>
                  <div className="label-overline text-slate-400">Locker key</div>
                  <div className="mt-1">{data.action_plan.locker_key_location}</div>
                </div>
              )}
              {data.action_plan.monthly_expense_estimate && (
                <div>
                  <div className="label-overline text-slate-400">Monthly household need</div>
                  <div className="mt-1 tabular-nums">
                    {formatCurrency(data.action_plan.monthly_expense_estimate, true)}
                  </div>
                </div>
              )}
            </div>
            {(data.action_plan.sections || []).map((sec) => (
              <div key={sec.key} className="mt-5">
                <div className="font-heading font-semibold">{sec.title}</div>
                <ul className="mt-2 space-y-1.5 text-sm text-slate-200">
                  {sec.items.map((it, i) => (
                    <li key={i} className="flex items-start gap-2">
                      {it.done
                        ? <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                        : <span className="h-4 w-4 rounded-full border border-slate-500 mt-0.5 shrink-0" />}
                      <span className={it.done ? "line-through text-slate-400" : ""}>{it.text}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </Card>
        )}

        {/* Emergency contacts */}
        {data.emergency_contacts && data.emergency_contacts.length > 0 && (
          <Card className="p-6" data-testid="share-emergency-contacts">
            <div className="font-heading font-semibold flex items-center gap-2">
              <Phone className="h-4 w-4" /> Emergency Contacts
            </div>
            <ul className="mt-4 divide-y divide-slate-100">
              {data.emergency_contacts.map((c, i) => (
                <li key={i} className="py-3 flex flex-wrap items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-medium">{c.full_name}</div>
                    <div className="text-xs text-slate-500">
                      {c.relationship} · {c.role}
                    </div>
                  </div>
                  <a href={`mailto:${c.email}`} className="text-sm inline-flex items-center gap-1 text-slate-700 hover:text-slate-900">
                    <Mail className="h-3.5 w-3.5" /> {c.email}
                  </a>
                </li>
              ))}
            </ul>
          </Card>
        )}

        {/* Modules */}
        {(data.modules || []).map((mod) => (
          mod.items.length > 0 && (
            <section key={mod.slug} data-testid={`share-module-${mod.slug}`}>
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-heading text-xl font-semibold">{mod.name}</h2>
                <Badge variant="outline" className="text-[10px]">{mod.items.length}</Badge>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {mod.items.map((it, idx) => (
                  <Card key={idx} className="p-4">
                    <div className="font-medium text-slate-900">{it.title}</div>
                    <dl className="mt-2 space-y-1 text-xs">
                      {Object.entries(it.data || {}).map(([k, v]) => (
                        v ? (
                          <div key={k} className="flex gap-2 justify-between">
                            <dt className="text-slate-500 shrink-0">{k.replace(/_/g, " ")}</dt>
                            <dd className="text-slate-800 text-right min-w-0 break-words">{String(v)}</dd>
                          </div>
                        ) : null
                      ))}
                    </dl>
                    {it.remarks && (
                      <div className="mt-2 text-xs italic text-slate-500 border-t border-slate-100 pt-2">
                        {it.remarks}
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </section>
          )
        ))}

        {/* Footer */}
        <footer className="pt-6 border-t border-slate-200 text-xs text-slate-500 flex items-center gap-2 justify-between flex-wrap">
          <div className="flex items-center gap-2">
            <Vault className="h-3 w-3" /> Family Vault — Read-only share
          </div>
          <div>Owner can revoke this link any time. Expires {expiresAt.toLocaleString()}.</div>
        </footer>
      </main>
    </div>
  );
}
